/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { PartyPicker } from '@/components/PartyPicker';
import { Button, ErrorText, Field, Input, Modal, PageHeader, Select, Spinner, Textarea } from '@/components/ui';
import { api, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtMoney } from '@/lib/format';
import { useDebounced, useDict } from '@/lib/hooks';

export function DealsPage() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const { data: pipes } = useQuery({ queryKey: ['pipelines'], queryFn: () => api.get('/pipelines') });
  const [pid, setPid] = useState<string>('');
  const [q, setQ] = useState('');
  const dq = useDebounced(q);
  const pipe = pipes?.find((p: any) => p.id === pid) ?? pipes?.[0];
  const { data: deals, isLoading } = useQuery({ enabled: !!pipe, queryKey: ['board', pipe?.id, dq], queryFn: () => api.get(`/pipelines/${pipe.id}/board${qs({ q: dq })}`) });
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<any>(null);
  const [lostFor, setLostFor] = useState<{ deal: any; stageId: string } | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const move = useMutation({
    mutationFn: (b: { id: string; stageId: string; lostReason?: string }) => api.post(`/deals/${b.id}/move`, b),
    onSuccess: () => { setLostFor(null); qc.invalidateQueries({ queryKey: ['board'] }); },
  });
  const drop = (stage: any) => {
    const d = deals?.find((x: any) => x.id === dragId);
    setDragId(null);
    if (!d || d.stageId === stage.id) return;
    if (stage.isLost) setLostFor({ deal: d, stageId: stage.id });
    else move.mutate({ id: d.id, stageId: stage.id });
  };
  if (!pipes) return <Spinner />;
  return (
    <>
      <PageHeader title="Воронка продаж" actions={<>
        {pipes.length > 1 && <Select value={pipe?.id} onChange={(e) => setPid(e.target.value)} options={pipes.map((p: any) => ({ value: p.id, label: p.name }))} />}
        <Input className="w-56" placeholder="Поиск" value={q} onChange={(e) => setQ(e.target.value)} />
        {can('deal.write') && <Button onClick={() => setCreating(true)}>Новая сделка</Button>}
      </>} />
      <ErrorText error={move.error} />
      {isLoading ? <Spinner /> : (
        <div className="flex gap-3 overflow-x-auto pb-4">
          {pipe.stages.map((s: any) => {
            const items = (deals ?? []).filter((d: any) => d.stageId === s.id);
            const sum = items.reduce((a: number, d: any) => a + Number(d.amount ?? 0), 0);
            return (
              <div key={s.id} className={clsx('flex w-72 shrink-0 flex-col rounded-lg bg-slate-100', dragId && 'ring-1 ring-brand-100')}
                onDragOver={(e) => e.preventDefault()} onDrop={() => drop(s)}>
                <div className="border-b-2 px-3 py-2" style={{ borderColor: s.color ?? '#94a3b8' }}>
                  <div className="text-sm font-semibold">{s.name} <span className="font-normal text-slate-500">{items.length}</span></div>
                  <div className="text-xs text-slate-500">{fmtMoney(sum)}</div>
                </div>
                <div className="flex-1 space-y-2 p-2">
                  {items.map((d: any) => {
                    const slaBreached = s.slaHours && !s.isWon && !s.isLost && Date.now() - new Date(d.stageChangedAt).getTime() > s.slaHours * 3600_000;
                    return (
                      <div key={d.id} draggable={can('deal.write')} onDragStart={() => setDragId(d.id)} onClick={() => setOpen(d)}
                        className={clsx('cursor-pointer rounded-md border bg-white p-2.5 text-sm shadow-sm hover:border-brand-500', slaBreached ? 'border-red-300' : 'border-slate-200')}>
                        <div className="font-medium">{d.title}</div>
                        <div className="text-xs text-slate-500">{d.partyName ?? d.contactName}{d.phone ? ` · ${d.phone}` : ''}</div>
                        <div className="mt-1 flex justify-between text-xs"><span className="tabular-nums">{d.amount ? fmtMoney(d.amount) : ''}</span>
                          {slaBreached && <span className="text-red-600">просрочен первый контакт</span>}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <Modal title="Новая сделка" open={creating} onClose={() => setCreating(false)}>
        {pipe && <DealForm pipelineId={pipe.id} onSaved={() => { setCreating(false); qc.invalidateQueries({ queryKey: ['board'] }); }} />}
      </Modal>
      <Modal title={open?.title ?? ''} open={!!open} onClose={() => setOpen(null)}>
        {open && <DealForm deal={open} pipelineId={pipe.id} onSaved={() => { setOpen(null); qc.invalidateQueries({ queryKey: ['board'] }); }} />}
      </Modal>
      <Modal title="Причина отказа" open={!!lostFor} onClose={() => setLostFor(null)}>
        {lostFor && <LostForm onSubmit={(r) => move.mutate({ id: lostFor.deal.id, stageId: lostFor.stageId, lostReason: r })} />}
      </Modal>
    </>
  );
}

function LostForm({ onSubmit }: { onSubmit: (r: string) => void }) {
  const { data } = useDict('lost_reason');
  const [r, setR] = useState('');
  return (
    <div className="space-y-3">
      <Select value={r} onChange={(e) => setR(e.target.value)} placeholder="Выберите причину" options={(data ?? []).map((x) => ({ value: x.name, label: x.name }))} />
      <div className="flex justify-end"><Button disabled={!r} onClick={() => onSubmit(r)}>Перенести в «Отказ»</Button></div>
    </div>
  );
}

function DealForm({ deal, pipelineId, onSaved }: { deal?: any; pipelineId: string; onSaved: () => void }) {
  const { data: sources } = useDict('lead_source');
  const { data: services } = useDict('service');
  const [f, setF] = useState({
    title: deal?.title ?? '', contactName: deal?.contactName ?? '', phone: deal?.phone ?? '', email: deal?.email ?? '',
    source: deal?.source ?? '', service: deal?.service ?? '', amount: deal?.amount ?? '', partyId: deal?.partyId ?? null, partyName: deal?.partyName ?? '',
  });
  const save = useMutation({
    mutationFn: () => {
      const body = { pipelineId, title: f.title, contactName: f.contactName || null, phone: f.phone || null, email: f.email || null,
        source: f.source || null, service: f.service || null, amount: f.amount || null, partyId: f.partyId };
      return deal ? api.patch(`/deals/${deal.id}`, body) : api.post('/deals', body);
    },
    onSuccess: onSaved,
  });
  const convert = useMutation({ mutationFn: () => api.post(`/deals/${deal.id}/convert`), onSuccess: (r) => setF({ ...f, partyId: r.partyId }) });
  const submit = (e: FormEvent) => { e.preventDefault(); save.mutate(); };
  return (
    <form onSubmit={submit} className="space-y-3">
      <Field label="Название"><Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
      <Field label="Клиент"><PartyPicker value={f.partyId} valueLabel={f.partyName} onChange={(id, n) => setF({ ...f, partyId: id, partyName: n ?? '' })} /></Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Контактное лицо"><Input value={f.contactName} onChange={(e) => setF({ ...f, contactName: e.target.value })} /></Field>
        <Field label="Телефон"><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
        <Field label="Почта"><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
        <Field label="Сумма, ₽"><Input inputMode="decimal" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value.replace(',', '.') })} /></Field>
        <Field label="Источник"><Select value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} placeholder="-" options={(sources ?? []).map((x) => ({ value: x.name, label: x.name }))} /></Field>
        <Field label="Услуга"><Select value={f.service} onChange={(e) => setF({ ...f, service: e.target.value })} placeholder="-" options={(services ?? []).map((x) => ({ value: x.name, label: x.name }))} /></Field>
      </div>
      <ErrorText error={save.error ?? convert.error} />
      <div className="flex items-center justify-between">
        <div>{deal && (f.partyId ? <Link className="text-sm text-brand-700 hover:underline" to={`/parties/${f.partyId}`}>Открыть карточку клиента</Link>
          : <Button type="button" variant="secondary" onClick={() => convert.mutate()}>Создать клиента</Button>)}</div>
        <Button type="submit" disabled={save.isPending}>Сохранить</Button>
      </div>
    </form>
  );
}
